import { useState } from 'preact/hooks';

import { strings } from '../messages';
import type { CheckoutOptions, WidgetRate } from '../types';
import { Rates } from './Rates';

interface Props extends CheckoutOptions {
  apiUrl: string;
  emit: (event: string, payload: unknown) => void;
}

/**
 * Checkout flow:
 *
 * 1. Show the standard Rates picker.
 * 2. When the user clicks a rate → ``rate-selected`` is dispatched
 *    and a confirmation row appears with the chosen rate summary +
 *    a primary CTA (default "Confirmer la livraison").
 * 3. When the user confirms → ``rate-purchased`` is dispatched on the
 *    widget and the optional ``onPurchase(rate)`` host callback is
 *    invoked. The widget itself **never** creates the shipment — the
 *    host site owns that flow (typical pattern: pass the rate into
 *    the merchant's checkout API).
 */
export function Checkout({ apiUrl, locale, emit, onPurchase, confirmLabel, ...rest }: Props) {
  const t = strings(locale);
  const [picked, setPicked] = useState<WidgetRate | null>(null);
  const [pending, setPending] = useState(false);
  const [purchased, setPurchased] = useState<WidgetRate | null>(null);

  return (
    <div>
      <Rates
        {...rest}
        apiUrl={apiUrl}
        locale={locale}
        emit={emit}
        onRateSelected={(rate) => {
          setPicked(rate);
          setPurchased(null);
          rest.onRateSelected?.(rate);
        }}
      />
      {picked && !purchased && (
        <div class="tsw-checkout-summary" role="group" aria-label={t.checkoutConfirm}>
          <div>
            <p style="margin:0;font-weight:600;">{picked.service}</p>
            <p class="tsw-muted tsw-mono" style="margin:0;">{picked.carrierName}</p>
            <p style="margin:4px 0 0;">
              {t.checkoutTotal}: <strong>{picked.totalCharge.toFixed(2)} {picked.currency}</strong>
            </p>
          </div>
          <div class="tsw-actions">
            <button
              type="button"
              class="tsw-button tsw-button-outline"
              onClick={() => setPicked(null)}
            >
              {t.checkoutChange}
            </button>
            <button
              type="button"
              class="tsw-button"
              disabled={pending}
              onClick={async () => {
                if (!picked) return;
                setPending(true);
                try {
                  await Promise.resolve(onPurchase?.(picked));
                  emit('rate-purchased', picked);
                  setPurchased(picked);
                } finally {
                  setPending(false);
                }
              }}
            >
              {pending ? t.trackRefreshing : confirmLabel ?? t.checkoutConfirm}
            </button>
          </div>
        </div>
      )}
      {purchased && (
        <p class="tsw-muted" style="margin-top:12px;" role="status" aria-live="polite">
          {t.checkoutThanks}
        </p>
      )}
    </div>
  );
}
